import React, { useEffect, useId, useRef, useState } from "react";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";
import { IntrinsicElementProps } from "@thelabnyc/thelabui/src/utils/types";

import { useMatchMedia } from "../Carousel/hooks/useMatchMedia";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface Props {
    children: React.ReactNode;
    durationInSeconds?: number;
    id?: string | undefined;
    ariaLabel?: string;
    attrs?: IntrinsicElementProps<HTMLDivElement>;
    onPauseChange?: (isPaused: boolean) => void;
}

/**
 * Marquee/ticker component with accessible play/pause controls.
 * - Duplicates children as needed to fill the viewport width
 * - Respects prefers-reduced-motion (pauses automatically)
 * - Play/pause button with hover-to-pause on desktop
 * - When paused, hides duplicates and allows horizontal scrolling
 */
export const MarqueeBlock = (props: Props) => {
    const marqueeId = useId();
    const root = useRef<HTMLDivElement | null>(null);
    const allTickerText = useRef<HTMLDivElement | null>(null);
    const [duplicates, setDuplicates] = useState(1);
    const prefersReducedMotion = useMatchMedia(
        "(prefers-reduced-motion: reduce)",
    );
    // "auto" follows prefers-reduced-motion; "user-*" locks to the
    // user's explicit choice and ignores the OS preference.
    // "user-playing" also temporarily suppresses hover-to-pause
    // (via ignoreHoverPause) so clicking play while hovering works.
    const [pauseState, setPauseState] = useState<
        "auto" | "user-paused" | "user-playing"
    >("auto");
    // True after clicking play while hovering; resets on mouse leave.
    // Prevents the CSS hover-to-pause from immediately re-pausing.
    const [ignoreHoverPause, setIgnoreHoverPause] = useState(false);

    const isPaused =
        pauseState === "user-paused" ||
        (pauseState === "auto" && prefersReducedMotion);

    useEffect(() => {
        props.onPauseChange?.(isPaused);
    }, [isPaused]);

    useEffect(() => {
        const wrapperWidth = root.current?.getBoundingClientRect().width;
        const allTickerWidth =
            allTickerText.current?.getBoundingClientRect().width;
        if (wrapperWidth && allTickerWidth) {
            setDuplicates(Math.ceil(wrapperWidth / allTickerWidth));
        }
    }, []);

    if (!props.children) return <></>;

    return (
        <section
            id={props.id}
            className={concatClassNames([
                styles.root,
                isPaused ? styles.rootPaused : null,
                ignoreHoverPause ? styles.rootIgnoreHoverPause : null,
            ])}
            style={{
                "--duration": props.durationInSeconds
                    ? `${props.durationInSeconds}s`
                    : "10s",
            }}
            ref={root}
            onMouseLeave={() => setIgnoreHoverPause(false)}
        >
            <Clickable
                aria-label={isPaused ? t`Play marquee` : t`Pause marquee`}
                aria-pressed={isPaused}
                aria-controls={marqueeId}
                className={styles.playPauseButton}
                onPress={() => {
                    setPauseState(isPaused ? "user-playing" : "user-paused");
                    setIgnoreHoverPause(isPaused);
                }}
            >
                <Svg
                    name={isPaused ? "play" : "pause"}
                    className={styles.playPauseIcon}
                />
            </Clickable>
            <div
                {...props.attrs}
                className={concatClassNames([
                    props.attrs?.className,
                    styles.viewport,
                ])}
            >
                <div
                    id={marqueeId}
                    className={styles.wrapper}
                    role="marquee"
                    aria-label={props.ariaLabel}
                >
                    <div className={styles.ticker} ref={allTickerText}>
                        {props.children}
                    </div>
                    {Array(duplicates)
                        .fill(0)
                        .map((_, index) => (
                            <div
                                key={`duplicates${index}`}
                                className={styles.ticker}
                                aria-hidden="true"
                            >
                                {props.children}
                            </div>
                        ))}
                </div>
            </div>
        </section>
    );
};
